"use client";

import { UploadCloud } from "lucide-react";
import { useTranslations } from "next-intl";
import Dropzone from "react-dropzone";
import { getDropFiles, preserveBatchLimit } from "@/lib/upload/file-drop";
import { cn } from "@/lib/utils";

/**
 * Self-contained drag-and-drop file picker for the media tools.
 * Always hands back an array; single-file callers take `files[0]`.
 * `compact` renders a one-line target — for rails where content already
 * exists and the tall empty-state invitation would waste space.
 */
export function MediaDropzone({
  onDrop,
  accept = { "audio/*": [], "video/*": [] },
  prompt,
  multiple = false,
  disabled = false,
  compact = false,
}: {
  onDrop: (files: File[]) => void;
  accept?: Record<string, string[]>;
  prompt?: string;
  multiple?: boolean;
  disabled?: boolean;
  compact?: boolean;
}) {
  const t = useTranslations("mediaTools.dropzone");

  return (
    <Dropzone
      getFilesFromEvent={getDropFiles}
      noPaste
      accept={accept}
      multiple={multiple}
      disabled={disabled}
      onDrop={(accepted, rejected) => {
        const selection = preserveBatchLimit(accepted, rejected);
        if (selection.accepted.length > 0) onDrop(selection.accepted);
      }}
    >
      {({ getRootProps, getInputProps, isDragActive }) => (
        <div {...getRootProps()}>
          <input {...getInputProps()} />
          {compact ? (
            <div
              className={cn(
                "flex w-full items-center justify-center gap-2 rounded border border-dashed border-foreground/40 px-3 py-2 text-foreground/70 text-sm",
                !disabled && "cursor-pointer hover:border-foreground/70 hover:text-foreground",
                disabled && "cursor-not-allowed opacity-50",
                isDragActive && "border-primary text-foreground"
              )}
            >
              <UploadCloud className="h-4 w-4 shrink-0 opacity-80" />
              <span className="truncate">{prompt ?? t("defaultPrompt")}</span>
            </div>
          ) : (
            <div
              className={cn(
                "flex w-full flex-col items-center justify-center rounded border-2 border-dashed border-foreground/80 px-4 py-16 text-center text-foreground",
                !disabled &&
                  "cursor-pointer bg-foreground/5 hover:border-foreground hover:bg-transparent",
                disabled && "cursor-not-allowed opacity-50",
                isDragActive && "border-primary"
              )}
            >
              <UploadCloud className="mb-4 h-8 w-8 opacity-80" />
              <p className="mb-1 text-xl">{prompt ?? t("defaultPrompt")}</p>
              <p className="text-base text-foreground/80">{t("clickOrDrag")}</p>
            </div>
          )}
        </div>
      )}
    </Dropzone>
  );
}
